/**
 * @fileoverview Template: the one and only error screen of the application.
 * @module components/templates/ErrorTemplate
 *
 * @description
 * Both error paths — router loader failures and render-time crashes — render
 * this template, so a user never sees two different error designs.
 */

import { useEffect, useRef } from "react"

import { ErrorActions, ErrorDetails } from "@/components/molecules"
import { getErrorPresentation, type AppError } from "@/lib/errors"

import "./ErrorTemplate.css"

/** Props accepted by {@link ErrorTemplate}. */
export interface ErrorTemplateProps {
	/** The normalised error to present. */
	readonly error: AppError
	/** Optional secondary line, e.g. an HTTP status text. */
	readonly statusText?: string | undefined
	/** Retry callback offered when recovery is possible without navigating. */
	readonly onRetry?: (() => void) | undefined
}

/**
 * Full-screen error state.
 *
 * @param props - The error, an optional status line and a retry callback.
 * @returns The rendered error screen, focused for assistive technology.
 */
export function ErrorTemplate({
	error,
	statusText,
	onRetry,
}: ErrorTemplateProps) {
	const containerRef = useRef<HTMLDivElement>(null)
	const presentation = getErrorPresentation({
		status: error.status,
		code: error.code,
	})

	// Move focus to the alert so screen readers announce the failure.
	useEffect(() => {
		containerRef.current?.focus()
	}, [])

	return (
		<div className="error-page">
			<div
				ref={containerRef}
				className="error-page__inner"
				role="alert"
				aria-live="polite"
				tabIndex={-1}
			>
				<svg
					className="error-page__illustration"
					role="img"
					aria-label={`Illustration : ${presentation.title}`}
				>
					<use href={presentation.image} />
				</svg>

				<h1 className="error-page__title">
					{error.status === undefined
						? presentation.title
						: `${error.status} — ${presentation.title}`}
				</h1>

				{statusText !== undefined && statusText !== "" && (
					<p className="error-page__status">{statusText}</p>
				)}

				<p className="error-page__message">{presentation.message}</p>

				<ErrorDetails error={error} />
				<ErrorActions onRetry={onRetry} />
			</div>
		</div>
	)
}
